import { useState, useEffect } from "react";
import { getGroupData, getGroupsData, saveGroupsToStorage } from '../assets/data';
import { getGroups as getGroupsFromSupabase } from '../utils/groupsService';
import { getLessonsForGroup, deleteLesson, getLessonsFromLocalStorage, saveLessonsToLocalStorage } from '../utils/lessonsService';

export function useGroupLessons({ groupId, navigate, groupLessons, setStudentLessons, showCustomModalConfig, setShowCustomModal }) {
  const [lessonSearchTerm, setLessonSearchTerm] = useState('');
  const [deletingLessonId, setDeletingLessonId] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [lessonToCopy, setLessonToCopy] = useState(null);
  const [copyTargetStudent, setCopyTargetStudent] = useState('');
  const [copyModalGroups, setCopyModalGroups] = useState(null);
  const [copyLessonSubmitting, setCopyLessonSubmitting] = useState(false);
  const [copyDate, setCopyDate] = useState('');

  const filteredLessons = groupLessons.filter(lesson =>
    (lesson.title || '').toLowerCase().includes(lessonSearchTerm.toLowerCase())
  );

  const handleCreateLesson = () => {
    navigate(`/lessons/new?groupId=${groupId}`);
  };

  const handleStartLesson = (lessonId) => {
    navigate(`/lessons/${lessonId}?groupId=${groupId}`);
  };

  // Функция для удаления урока
  const handleDeleteLesson = (lessonId) => {
    showCustomModalConfig({
      title: 'Delete Lesson',
      message: 'Are you sure you want to delete this lesson? This action cannot be undone.',
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        setShowCustomModal(false);
        setDeletingLessonId(lessonId);
        try {
          await deleteLesson(lessonId);
        } catch (err) {
          console.error('Error deleting lesson from Supabase:', err);
          setDeletingLessonId(null);
          showCustomModalConfig({
            title: 'Error',
            message: err?.message || 'Failed to delete lesson.',
            type: 'error',
            confirmText: 'OK'
          });
          return;
        }
        // Удаляем урок из списка уроков студента
        setStudentLessons(prev => prev.filter(lesson => lesson.id !== lessonId));

        // Удаляем из основного хранилища novas_lessons_data
        const allLessons = getLessonsFromLocalStorage();
        const updatedLessons = allLessons.filter(lesson => lesson.id !== lessonId);
        saveLessonsToLocalStorage(updatedLessons);

        // Удаляем урок из профиля группы, если он там есть
        const groupData = getGroupData(groupId);
        if (groupData && groupData.lessons) {
          const updatedGroupData = {
            ...groupData,
            lessons: groupData.lessons.filter(lesson => lesson.id !== lessonId)
          };

          const allGroups = getGroupsData();
          allGroups[groupId] = updatedGroupData;
          saveGroupsToStorage(allGroups);
        }

        console.log('Lesson deleted:', lessonId);

        // Принудительно обновляем список уроков после удаления
        setTimeout(() => {
          getLessonsForGroup(groupId).then(lessons => {
            setStudentLessons(lessons);
          }).catch(error => {
            console.error('Error loading lessons:', error);
          });
        }, 100);

        // Закрываем модальное окно
        setDeletingLessonId(null);
      }
    });
  };

  // Функция для изменения статуса урока
  const handleLessonStatusChange = (lessonId, newStatus) => {
    // Обновляем статус в списке уроков студента
    setStudentLessons(prev => prev.map(lesson =>
      lesson.id === lessonId ? { ...lesson, status: newStatus } : lesson
    ));

    // Обновляем в основном хранилище novas_lessons_data
    const allLessons = getLessonsFromLocalStorage();
    const updatedLessons = allLessons.map(lesson =>
      lesson.id === lessonId ? { ...lesson, status: newStatus } : lesson
    );
    saveLessonsToLocalStorage(updatedLessons);

    // Обновляем урок в профиле группы, если он там есть
    const groupData = getGroupData(groupId);
    if (groupData && groupData.lessons) {
      const updatedGroupData = {
        ...groupData,
        lessons: groupData.lessons.map(lesson =>
          lesson.id === lessonId ? { ...lesson, status: newStatus } : lesson
        )
      };

      const allGroups = getGroupsData();
      allGroups[groupId] = updatedGroupData;
      saveGroupsToStorage(allGroups);
    }

    console.log('Lesson status updated:', lessonId, 'to', newStatus);

    // Принудительно обновляем список уроков после изменения статуса
    setTimeout(() => {
      const lessons = getLessonsForGroup(groupId);
      setStudentLessons(lessons);
    }, 100);
  };

  const normalizeDateForInput = (dateVal) => {
    if (!dateVal) return new Date().toISOString().split('T')[0];
    const str = String(dateVal);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    return new Date().toISOString().split('T')[0];
  };

  // Функция для копирования урока
  const handleCopyLesson = (lesson) => {
    setLessonToCopy(lesson);
    setCopyTargetStudent('');
    setCopyDate(normalizeDateForInput(lesson?.date));
    setShowCopyModal(true);
  };

  useEffect(() => {
    if (!showCopyModal) return;
    getGroupsFromSupabase()
      .then((supabaseGroups) => {
        setCopyModalGroups(supabaseGroups || {});
      })
      .catch(() => setCopyModalGroups({}));
  }, [showCopyModal]);

  // Функция для подтверждения копирования урока
  const confirmCopyLesson = () => {
    console.log('=== COPY LESSON DEBUG ===');
    console.log('copyTargetStudent:', copyTargetStudent);
    console.log('copyDate:', copyDate);
    console.log('lessonToCopy:', lessonToCopy);

    if (!copyTargetStudent || copyTargetStudent === '') {
      showCustomModalConfig({
        title: 'Error',
        message: 'Please select a target group.',
        type: 'error'
      });
      return;
    }

    if (!copyDate) {
      showCustomModalConfig({
        title: 'Error',
        message: 'Please select a date for the copied lesson.',
        type: 'error'
      });
      return;
    }

    if (!lessonToCopy) {
      showCustomModalConfig({
        title: 'Error',
        message: 'No lesson selected for copying.',
        type: 'error'
      });
      return;
    }

    setCopyLessonSubmitting(true);
    // Даём один кадр на отрисовку "Copying…"
    setTimeout(() => {
      try {
        runConfirmCopyLesson();
      } finally {
        setCopyLessonSubmitting(false);
      }
    }, 0);
  };

  const runConfirmCopyLesson = () => {
    // Получаем список всех групп (local + Supabase), чтобы выбранная группа была в списке
    const groups = { ...getGroupsData(), ...(copyModalGroups || {}) };
    console.log('Available groups:', groups);

    // Ищем полные данные урока во всех возможных источниках
    let originalLesson = null;

    // 1. Ищем в novas_lessons_data (основное хранилище уроков)
    const allLessons = getLessonsFromLocalStorage();
    console.log('Lessons in novas_lessons_data:', allLessons);
    originalLesson = allLessons.find(lesson => lesson.id === lessonToCopy.id);
    console.log('Found in novas_lessons_data:', originalLesson);

    // 2. Если все еще не нашли, ищем по title (так как ID могут отличаться)
    if (!originalLesson) {
      console.log('Searching by title...');
      originalLesson = allLessons.find(lesson => lesson.title === lessonToCopy.title);
      console.log('Found by title in novas_lessons_data:', originalLesson);
    }

    // 3. Если все еще не нашли, используем данные из lessonToCopy
    if (!originalLesson) {
      console.log('Using lessonToCopy as originalLesson');
      originalLesson = lessonToCopy;
    }

    console.log('Final originalLesson:', originalLesson);
    console.log('originalLesson.containers:', originalLesson?.containers);
    console.log('originalLesson.whiteboards:', originalLesson?.whiteboards);
    console.log('originalLesson.editorContents:', originalLesson?.editorContents);
    console.log('originalLesson.whiteboardContents:', originalLesson?.whiteboardContents);

    // Создаем копию урока с полным содержимым
    const newLesson = {
      ...lessonToCopy,
      id: Date.now().toString(), // Новый уникальный ID
      groupId: copyTargetStudent,
      date: copyDate,
      status: 'in_progress', // Сбрасываем статус
      originalLessonId: lessonToCopy.id, // Сохраняем ссылку на оригинал
      // Копируем полное содержимое урока
      containers: originalLesson?.containers || lessonToCopy.containers || [{ id: 1, type: 'editor' }],
      whiteboards: originalLesson?.whiteboards || lessonToCopy.whiteboards || [],
      editorContents: originalLesson?.editorContents || lessonToCopy.editorContents || [],
      whiteboardContents: originalLesson?.whiteboardContents || lessonToCopy.whiteboardContents || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log('New lesson with content:', newLesson);
    console.log('New lesson.containers:', newLesson.containers);
    console.log('New lesson.editorContents:', newLesson.editorContents);

    // Сохраняем урок в основное хранилище (novas_lessons_data)
    const currentAllLessons = getLessonsFromLocalStorage();
    currentAllLessons.push(newLesson);
    saveLessonsToLocalStorage(currentAllLessons);
    console.log('Saved to novas_lessons_data:', currentAllLessons);

    // Добавляем урок только в профиль целевого студента
    const targetGroupData = groups[copyTargetStudent];
    if (targetGroupData) {
      const updatedStudentData = {
        ...targetGroupData,
        lessons: [...(targetGroupData.lessons || []), newLesson]
      };

      // Обновляем данные студента в localStorage
      const allStudents = getGroupsData();
      allStudents[copyTargetStudent] = updatedStudentData;
      saveGroupsToStorage(allStudents);

      console.log('Updated target group data:', updatedStudentData);

      // Если копируем в профиль текущего студента, обновляем список уроков
      if (copyTargetStudent === groupId) {
        const updatedLessons = [...groupLessons, newLesson];
        setStudentLessons(updatedLessons);
        console.log('Updated current group lessons:', updatedLessons);
      }
    }

    // Проверяем, что скопированный урок содержит контент
    console.log('=== COPIED LESSON CONTENT CHECK ===');
    console.log('Copied lesson ID:', newLesson.id);
    console.log('Copied lesson title:', newLesson.title);
    console.log('Has containers:', !!newLesson.containers && newLesson.containers.length > 0);
    console.log('Has whiteboards:', !!newLesson.whiteboards && newLesson.whiteboards.length > 0);
    console.log('Has editor contents:', !!newLesson.editorContents && newLesson.editorContents.length > 0);
    console.log('Has whiteboard contents:', !!newLesson.whiteboardContents && newLesson.whiteboardContents.length > 0);
    console.log('Containers:', newLesson.containers);
    console.log('Editor contents:', newLesson.editorContents);

    // Закрываем модальное окно
    setShowCopyModal(false);
    setLessonToCopy(null);
    setCopyTargetStudent('');
    setCopyDate('');

    showCustomModalConfig({
      title: '✅ Lesson Copied Successfully!',
      message: `Lesson "${lessonToCopy.title}" has been copied to ${groups[copyTargetStudent]?.name || copyTargetStudent}'s profile successfully!\n\nYou can now find this lesson in the group's profile under the Lessons tab.`,
      type: 'success',
      confirmText: 'OK',
      onConfirm: () => {
        // Можно добавить дополнительное действие, например, переход к профилю студента
        console.log('Lesson copied successfully');
      }
    });
  };

  return {
    lessonSearchTerm, setLessonSearchTerm,
    deletingLessonId, setDeletingLessonId,
    showCopyModal, setShowCopyModal,
    lessonToCopy, setLessonToCopy,
    copyTargetStudent, setCopyTargetStudent,
    copyModalGroups, setCopyModalGroups,
    copyLessonSubmitting, setCopyLessonSubmitting,
    copyDate, setCopyDate,
    filteredLessons,
    handleCreateLesson, handleStartLesson, handleDeleteLesson, handleLessonStatusChange,
    normalizeDateForInput, handleCopyLesson, confirmCopyLesson, runConfirmCopyLesson,
  };
}
